import * as XLSX from 'xlsx';
import { ProjectRecord, ProjectsFiltersState } from '@/lib/projects/project-types';

/**
 * Format a date string (ISO or similar) to dd.mm.yyyy for export.
 * Returns empty string if value is missing/invalid.
 */
function formatDateForExport(value?: string | Date | null): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

/**
 * Truncate text to maxLen characters, appending "…" if truncated.
 */
function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + '…';
}

/**
 * Clean text: trim whitespace and return empty string for null/undefined.
 */
function cleanText(value?: string | null): string {
  if (!value) return '';
  return value.trim();
}

export interface ExportMetadata {
  filters?: ProjectsFiltersState;
  searchQuery?: string;
  totalProjectsCount?: number;
}

interface ExportArgs {
  rows: ProjectRecord[];
  fileBaseName?: string;
  metadata?: ExportMetadata;
}

interface ExportResult {
  exportedCount: number;
  fileName: string;
}

function mapProjectsToExportRows(rows: ProjectRecord[]) {
  return rows.map((p) => ({
    'Project name': cleanText(p.projectName),
    Organisation: cleanText(p.organizationName),
    Country: p.country.join(', '),
    Sector: cleanText(p.sector),
    'Start date': formatDateForExport(p.startDate),
    'End date': formatDateForExport(p.endDate),
    'Executive summary': truncate(cleanText(p.executiveSummary), 240),
    'Project URL': cleanText(p.projectLink),
    'CFP URL': cleanText(p.callForProposalLink),
    'Source domain': cleanText(p.sourceDomain),
    Updated: formatDateForExport(p.updatedAt),
  }));
}

function buildExportFileName(baseName: string, extension: string): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${baseName}-${yyyy}-${mm}-${dd}.${extension}`;
}

/**
 * Export an array of ProjectRecord to an Excel .xlsx file.
 * Uses SheetJS (xlsx) entirely in the browser — no backend needed.
 */
export function exportProjectsToXlsx({
  rows,
  fileBaseName = 'dc-projects',
  metadata,
}: ExportArgs): ExportResult {
  const exportRows = mapProjectsToExportRows(rows);

  const ws = XLSX.utils.json_to_sheet(exportRows);

  // Column widths (wch = width in characters)
  ws['!cols'] = [
    { wch: 40 }, // Project name
    { wch: 24 }, // Organisation
    { wch: 16 }, // Country
    { wch: 24 }, // Sector
    { wch: 14 }, // Start date
    { wch: 14 }, // End date
    { wch: 60 }, // Executive summary
    { wch: 50 }, // Project URL
    { wch: 50 }, // CFP URL
    { wch: 20 }, // Source domain
    { wch: 14 }, // Updated
  ];

  // Freeze header row
  ws['!views'] = [{ state: 'frozen', ySplit: 1 }];

  // Autofilter on header row
  const lastCol = XLSX.utils.encode_col(10); // Column K (0-indexed = 10)
  ws['!autofilter'] = { ref: `A1:${lastCol}${exportRows.length + 1}` };

  // Bold headers (row 1)
  const headerCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'H1', 'I1', 'J1', 'K1'];
  headerCells.forEach((cell) => {
    if (ws[cell]) {
      ws[cell].s = {
        font: { bold: true },
      };
    }
  });

  // Convert URLs to hyperlinks
  exportRows.forEach((row, idx) => {
    const rowNum = idx + 2; // +2 because row 1 is header, and idx is 0-based
    
    // Project URL in column H
    const projectUrlCell = `H${rowNum}`;
    if (ws[projectUrlCell] && row['Project URL']) {
      ws[projectUrlCell].l = { Target: row['Project URL'] };
      ws[projectUrlCell].s = { font: { color: { rgb: '0563C1' }, underline: true } };
    }

    // CFP URL in column I
    const cfpUrlCell = `I${rowNum}`;
    if (ws[cfpUrlCell] && row['CFP URL']) {
      ws[cfpUrlCell].l = { Target: row['CFP URL'] };
      ws[cfpUrlCell].s = { font: { color: { rgb: '0563C1' }, underline: true } };
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Projects');

  // Add Export Info sheet if metadata is provided
  if (metadata) {
    const infoSheet = createExportInfoSheet(rows.length, metadata);
    XLSX.utils.book_append_sheet(wb, infoSheet, 'Export Info');
  }

  const fileName = buildExportFileName(fileBaseName, 'xlsx');
  XLSX.writeFile(wb, fileName);

  return { exportedCount: rows.length, fileName };
}

/**
 * Create an Export Info sheet with metadata about the export.
 */
function createExportInfoSheet(exportedCount: number, metadata: ExportMetadata) {
  const now = new Date();
  const exportDateTime = `${formatDateForExport(now)} ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;

  const info: Record<string, string | number>[] = [
    { Field: 'Export Date/Time', Value: exportDateTime },
    { Field: 'Exported Projects', Value: exportedCount },
  ];

  if (metadata.totalProjectsCount !== undefined) {
    info.push({ Field: 'Total Projects (before filters)', Value: metadata.totalProjectsCount });
  }

  if (metadata.searchQuery) {
    info.push({ Field: 'Search Query', Value: metadata.searchQuery });
  }

  if (metadata.filters) {
    const f = metadata.filters;
    
    if (f.selectedCountries.length > 0) {
      info.push({ Field: 'Countries Filter', Value: f.selectedCountries.join(', ') });
    }
    
    if (f.selectedOrganizations.length > 0) {
      info.push({ Field: 'Organizations Filter', Value: f.selectedOrganizations.join(', ') });
    }
    
    if (f.selectedSectors.length > 0) {
      info.push({ Field: 'Sectors Filter', Value: f.selectedSectors.join(', ') });
    }
    
    if (f.selectedProductGroups.length > 0) {
      info.push({ Field: 'Product Groups Filter', Value: f.selectedProductGroups.join(', ') });
    }
    
    if (f.cfpMode !== 'any') {
      const cfpLabel = f.cfpMode === 'hasCfp' ? 'With CFP only' : 'All';
      info.push({ Field: 'CFP Filter', Value: cfpLabel });
    }
    
    if (f.sortMode !== 'relevance') {
      const sortLabel = f.sortMode === 'newest' ? 'Newest first' : 'End date';
      info.push({ Field: 'Sort Mode', Value: sortLabel });
    }
  }

  const ws = XLSX.utils.json_to_sheet(info);
  
  // Column widths
  ws['!cols'] = [
    { wch: 30 }, // Field
    { wch: 50 }, // Value
  ];

  // Bold headers
  if (ws['A1']) ws['A1'].s = { font: { bold: true } };
  if (ws['B1']) ws['B1'].s = { font: { bold: true } };

  return ws;
}

/**
 * Export an array of ProjectRecord to a CSV file (browser download).
 */
export function exportProjectsToCsv({
  rows,
  fileBaseName = 'dc-projects',
  metadata,
}: ExportArgs): ExportResult {
  const exportRows = mapProjectsToExportRows(rows);

  const ws = XLSX.utils.json_to_sheet(exportRows);
  const csv = XLSX.utils.sheet_to_csv(ws);

  const fileName = buildExportFileName(fileBaseName, 'csv');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);

  return { exportedCount: rows.length, fileName };
}
